import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { IconSparklesFilled } from "@tabler/icons-react";
import {
  Box,
  Container,
  Group,
  Text,
  Center,
  Grid,
  Stack,
  Paper,
  Flex,
  Avatar,
} from "@mantine/core";

import { EMPTY_DRAFT, formatApiToDraft, mapFrontToApiPayload } from "./utils";
import { HeroIntro } from "./HeroIntro";
import { ThinkingTimeline } from "./ThinkingTimeline";
import { ChatWidget } from "./ChatWidgets";
import { AgentPreviewCard } from "./AgentPreviewCard";
import { FinalScreen } from "./FinalScreen";
import { MobilePreviewBar } from "./MobilePreviewBar";
import { createAgent } from "../../../../api/AgentsAPI";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";

const STEPS = [
  {
    kind: "recap",
    message: (d) =>
      `Ça y est, j'ai fini l'analyse ! Voici ce que j'ai compris — corrigez-moi si besoin.`,
  },
  {
    kind: "name",
    message: () =>
      "Donnons vie à votre agent. Comment souhaitez-vous l'appeler ? J'ai une petite idée, mais vous décidez.",
  },
  {
    kind: "avatar",
    message: (d) => `Superbe. Maintenant, choisissez le visage de ${d.name}.`,
  },
  { kind: "gender", message: (d) => `Et quel genre pour ${d.name} ?` },
  {
    kind: "language",
    message: (d) =>
      // Reformulé avec une inversion de verbe sur le nom
      `Dans quelle langue s'exprimera ${d.name} avec vos prospects ?`,
  },
  {
    kind: "integration",
    message: (d) =>
      // Remplacement de "qu'il devra" par une répétition du nom ou une tournure neutre
      `C'est presque prêt ! Pour que ${d.name} puisse prospecter en votre nom, connectez les comptes Email et/ou LinkedIn que ${d.name} devra utiliser.`,
  },
  {
    kind: "valueProps",
    message: (d) =>
      // Utilisation de l'infinitif
      `Passons à la stratégie. Sur quelle promesse centrer le discours de ${d.name} ? J'en ai préparé ${d.valueProps.length}.`,
  },
  {
    kind: "tone",
    message: (d) =>
      // Inversion de verbe sur le nom
      `Dernier réglage de personnalité : quel ton devra adopter ${d.name} ?`,
  },
  {
    kind: "objective",
    message: (d) =>
      // Utilisation du "vous" pour enlever le pronom de l'agent
      `Pour aller plus loin, souhaitez-vous fixer un objectif précis à ${d.name} lors de vos campagnes ?`,
  },
  {
    kind: "instructions",
    message: (d) =>
      `Parfait. Voici les consignes que je recommande pour ${d.name}. Ajustez-les comme vous le souhaitez.`,
  },
];

let uid = 0;
const nextId = () => ++uid;

function AssistantAvatar() {
  return (
    <Avatar
      variant="gradient"
      gradient={{
        from: "var(--mantine-color-primary-4)",
        to: "var(--mantine-color-primary-7)",
        deg: 45,
      }}
      radius="xl"
      size={36}
      style={{ flexShrink: 0 }}
    >
      <IconSparklesFilled size={20} />
    </Avatar>
  );
}

function TypingDots() {
  return (
    <Paper
      withBorder
      shadow="sm"
      p="sm"
      radius="xl"
      style={{
        borderTopLeftRadius: "var(--mantine-radius-sm)",
        borderColor: "var(--mantine-color-gray-3)",
      }}
    >
      <Group gap={6} align="center">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "var(--mantine-color-gray-5)",
            }}
            animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
            transition={{
              duration: 0.9,
              repeat: Number.POSITIVE_INFINITY,
              delay: i * 0.15,
            }}
          />
        ))}
      </Group>
    </Paper>
  );
}

export function AgentBuilder() {
  const [phase, setPhase] = useState("intro");
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [turns, setTurns] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [awaiting, setAwaiting] = useState(false);
  const [typing, setTyping] = useState(false);
  const [created, setCreated] = useState(false);
  const [creating, setCreating] = useState(false);

  const scrollRef = useRef(null);
  const draftRef = useRef(EMPTY_DRAFT);
  const stepRef = useRef(0);
  const askedRef = useRef(-1);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [turns, typing, awaiting]);

  const askStep = useCallback((index, currentDraft) => {
    if (askedRef.current === index) return;
    askedRef.current = index;
    if (index >= STEPS.length) {
      setTimeout(() => setPhase("final"), 500);
      return;
    }
    setAwaiting(false);
    setTyping(true);
    setTimeout(() => {
      const step = STEPS[index];
      setTurns((t) => [
        ...t,
        {
          id: nextId(),
          role: "assistant",
          text: step.message(currentDraft),
          widgetKind: step.kind,
        },
      ]);
      setTyping(false);
      setAwaiting(true);
    }, 950);
  }, []);

  const handleStart = (input) => {
    const d = { ...draftRef.current, companyInput: input };
    draftRef.current = d;
    setDraft(d);
    setPhase("thinking");
  };

  const handleBriefError = (error) => {
    console.error("Erreur lors de la génération du brief :", error);
    notifications.show({
      message:
        "Une erreur est survenue lors de l'analyse de votre entreprise. Veuillez réessayer.",
      color: "red",
    });
    setPhase("intro");
  };

  const handleThinkingDone = useCallback(
    (result) => {
      const analyzed = {
        ...draftRef.current,
        ...formatApiToDraft(result, draftRef.current.companyInput),
      };
      draftRef.current = analyzed;
      setDraft(analyzed);
      setPhase("chat");
      stepRef.current = 0;
      setStepIndex(0);
      askStep(0, analyzed);
    },
    [askStep],
  );

  const handleWidgetSubmit = useCallback(
    (patch, summary) => {
      const newDraft = { ...draftRef.current, ...patch };
      draftRef.current = newDraft;
      setDraft(newDraft);
      setAwaiting(false);
      setTurns((t) => [...t, { id: nextId(), role: "user", text: summary }]);
      const next = stepRef.current + 1;
      stepRef.current = next;
      setStepIndex(next);
      askStep(next, newDraft);
    },
    [askStep],
  );

  const handleRestart = () => {
    draftRef.current = EMPTY_DRAFT;
    stepRef.current = 0;
    askedRef.current = -1;
    setDraft(EMPTY_DRAFT);
    setTurns([]);
    setStepIndex(0);
    setAwaiting(false);
    setTyping(false);
    setCreated(false);
    setPhase("intro");
  };

  const navigate = useNavigate();
  const handleCreate = () => {
    console.log("draft final :", draftRef.current);
    console.log("payload", mapFrontToApiPayload(draftRef.current));

    setCreating(true);
    createAgent(mapFrontToApiPayload(draftRef.current))
      .then((r) => {
        notifications.show({
          title: "Agent créé !",
          message: `Votre agent ${draftRef.current.name} a été créé avec succès.`,
          color: "green",
        });
        navigate(`/agents/edit/${r.data.agent.uniqid}`);
      })
      .finally(() => setCreating(false));
  };

  const activeTurnId = awaiting
    ? [...turns].reverse().find((t) => t.role === "assistant" && t.widgetKind)
        ?.id
    : undefined;

  return (
    <Box pos="relative" mih="70vh">
      <AnimatePresence mode="wait">
        {phase === "intro" && (
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <HeroIntro onStart={handleStart} />
          </motion.div>
        )}

        {phase === "thinking" && (
          <motion.div
            key="thinking"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <Center mih="calc(100vh - 4rem)" p="xl">
              <ThinkingTimeline
                draftRef={draftRef}
                onComplete={handleThinkingDone}
                onError={handleBriefError}
              />
            </Center>
          </motion.div>
        )}

        {phase === "chat" && (
          <motion.div
            key="chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Container size="xl" py="xl">
              <Grid gutter="xl">
                {/* Conversation (Colonnes principales) */}
                <Grid.Col span={{ base: 12, lg: 8 }}>
                  <Stack gap="md">
                    <MobilePreviewBar draft={draft} />

                    <Box
                      ref={scrollRef}
                      style={{
                        maxHeight: "calc(100vh - 11rem)",
                        overflowY: "auto",
                        paddingBottom: "1rem",
                        paddingRight: "4px",
                      }}
                    >
                      <Stack gap="xl">
                        {turns.map((turn) =>
                          turn.role === "assistant" ? (
                            <motion.div
                              key={turn.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                            >
                              <Flex gap="md" align="flex-start">
                                <AssistantAvatar />
                                <Stack
                                  gap="sm"
                                  style={{ flex: 1, minWidth: 0 }}
                                >
                                  <Paper
                                    radius="xl"
                                    p="md"
                                    shadow="sm"
                                    withBorder
                                    style={{
                                      borderTopLeftRadius:
                                        "var(--mantine-radius-sm)",
                                      display: "inline-block",
                                      width: "fit-content",
                                    }}
                                  >
                                    <Text size="lg" lh={1.6}>
                                      {turn.text}
                                    </Text>
                                  </Paper>

                                  {turn.widgetKind &&
                                    turn.id === activeTurnId && (
                                      <motion.div
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.15 }}
                                      >
                                        <Paper
                                          radius="xl"
                                          withBorder
                                          shadow="sm"
                                          p="md"
                                          bg="rgba(255, 255, 255, 0.6)"
                                          style={{
                                            backdropFilter: "blur(10px)",
                                          }}
                                        >
                                          <ChatWidget
                                            kind={turn.widgetKind}
                                            draft={draft}
                                            onSubmit={handleWidgetSubmit}
                                          />
                                        </Paper>
                                      </motion.div>
                                    )}
                                </Stack>
                              </Flex>
                            </motion.div>
                          ) : (
                            <motion.div
                              key={turn.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                            >
                              <Flex justify="flex-end">
                                <Paper
                                  bg="primary"
                                  c="white"
                                  radius="xl"
                                  p="md"
                                  shadow="sm"
                                  maw="85%"
                                  style={{
                                    borderTopRightRadius:
                                      "var(--mantine-radius-sm)",
                                  }}
                                >
                                  <Text size="lg" lh={1.6}>
                                    {turn.text}
                                  </Text>
                                </Paper>
                              </Flex>
                            </motion.div>
                          ),
                        )}

                        {typing && (
                          <Flex gap="md" align="flex-start">
                            <AssistantAvatar />
                            <TypingDots />
                          </Flex>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Grid.Col>

                {/* Live preview (Barre latérale) */}
                <Grid.Col span={{ base: 12, lg: 4 }} visibleFrom="lg">
                  <Box pos="sticky" top={96}>
                    <AgentPreviewCard draft={draft} />
                  </Box>
                </Grid.Col>
              </Grid>
            </Container>
          </motion.div>
        )}

        {phase === "final" && (
          <motion.div
            key="final"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <FinalScreen
              draft={draft}
              created={created}
              onCreate={handleCreate}
              onRestart={handleRestart}
              creating={creating}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
